import React, { useEffect, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import "../../styles/Home-page-component/Home.css";

/* ══════════════════════════════════════
   PARTICLE CANVAS  (same as CTA)
══════════════════════════════════════ */
function ParticleField() {
  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let W, H, pts, raf;

    const resize = () => {
      W = canvas.width  = canvas.offsetWidth;
      H = canvas.height = canvas.offsetHeight;
    };

    const make = () => ({
      x: Math.random() * W,
      y: Math.random() * H,
      vx: (Math.random() - 0.5) * 0.3,
      vy: (Math.random() - 0.5) * 0.3,
      r: Math.random() * 1.4 + 0.4,
      a: Math.random() * 0.5 + 0.08,
    });

    const init = () => { resize(); pts = Array.from({ length: 80 }, make); };

    const draw = () => {
      ctx.clearRect(0, 0, W, H);
      pts.forEach(p => {
        p.x += p.vx; p.y += p.vy;
        if (p.x < 0) p.x = W; if (p.x > W) p.x = 0;
        if (p.y < 0) p.y = H; if (p.y > H) p.y = 0;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(47,128,237,${p.a})`;
        ctx.fill();
      });
      for (let i = 0; i < pts.length; i++) {
        for (let j = i + 1; j < pts.length; j++) {
          const dx = pts[i].x - pts[j].x, dy = pts[i].y - pts[j].y;
          const d = Math.sqrt(dx * dx + dy * dy);
          if (d < 100) {
            ctx.beginPath();
            ctx.moveTo(pts[i].x, pts[i].y);
            ctx.lineTo(pts[j].x, pts[j].y);
            ctx.strokeStyle = `rgba(47,128,237,${0.1 * (1 - d / 100)})`;
            ctx.lineWidth = 0.6;
            ctx.stroke();
          }
        }
      }
      raf = requestAnimationFrame(draw);
    };

    init(); draw();
    window.addEventListener("resize", init);
    return () => { cancelAnimationFrame(raf); window.removeEventListener("resize", init); };
  }, []);
  return <canvas ref={canvasRef} className="hero-canvas" aria-hidden />;
}

/* ══════════════════════════════════════
   ANIMATED COUNTER
══════════════════════════════════════ */
function Counter({ to, prefix = "+", suffix = "" }) {
  const [val, setVal] = useState(0);
  const ref = useRef(null);
  const fired = useRef(false);
  useEffect(() => {
    const io = new IntersectionObserver(([e]) => {
      if (e.isIntersecting && !fired.current) {
        fired.current = true;
        const t0 = performance.now(), dur = 1800;
        const run = (now) => {
          const p = Math.min((now - t0) / dur, 1);
          const ease = 1 - (1 - p) ** 3;
          setVal(Math.round(ease * to));
          if (p < 1) requestAnimationFrame(run);
        };
        requestAnimationFrame(run);
      }
    }, { threshold: 0.5 });
    if (ref.current) io.observe(ref.current);
    return () => io.disconnect();
  }, [to]);
  return <span ref={ref}>{prefix}{val >= 1000 ? `${(val / 1000).toFixed(0)}k` : val}{suffix}</span>;
}

/* ══════════════════════════════════════
   ANIMATED PROGRESS BAR
══════════════════════════════════════ */
function ProgressBar({ pct, color = "var(--blue)", delay = 0 }) {
  const [w, setW] = useState(0);
  const ref = useRef(null);
  const fired = useRef(false);
  useEffect(() => {
    const io = new IntersectionObserver(([e]) => {
      if (e.isIntersecting && !fired.current) {
        fired.current = true;
        setTimeout(() => {
          const t0 = performance.now(), dur = 1200;
          const run = (now) => {
            const p = Math.min((now - t0) / dur, 1);
            const ease = 1 - (1 - p) ** 3;
            setW(ease * pct);
            if (p < 1) requestAnimationFrame(run);
          };
          requestAnimationFrame(run);
        }, delay);
      }
    }, { threshold: 0.4 });
    if (ref.current) io.observe(ref.current);
    return () => io.disconnect();
  }, [pct, delay]);
  return (
    <div className="prog-track" ref={ref}>
      <div className="prog-fill" style={{ width: `${w}%`, background: color }} />
    </div>
  );
}

/* ══════════════════════════════════════
   MAGNETIC BUTTON
══════════════════════════════════════ */
function MagBtn({ children, variant, onClick }) {
  const ref = useRef(null);
  const onMove = useCallback((e) => {
    const b = ref.current; if (!b) return;
    const r = b.getBoundingClientRect();
    const dx = (e.clientX - (r.left + r.width / 2)) * 0.25;
    const dy = (e.clientY - (r.top  + r.height / 2)) * 0.25;
    b.style.transform = `translate(${dx}px,${dy}px) scale(1.04)`;
  }, []);
  const onLeave = useCallback(() => {
    if (ref.current) ref.current.style.transform = "translate(0,0) scale(1)";
  }, []);
  return (
    <button
      ref={ref} className={`hero-btn ${variant}`}
      onMouseMove={onMove} onMouseLeave={onLeave} onClick={onClick}
    >
      <span className="btn-txt">{children}</span>
      {variant === "primary" && <span className="btn-shine" />}
    </button>
  );
}

/* ══════════════════════════════════════
   HERO CAROUSEL
══════════════════════════════════════ */
function HeroCarousel() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [imageErrors, setImageErrors] = useState({});
  
  const images = [
    { src: 'https://res.cloudinary.com/dmzjgqy3v/image/upload/v1775585430/img3_au0n8g.pngccd', alt: 'Demo Image 1' },
    { src: 'https://res.cloudinary.com/dmzjgqy3v/image/upload/v1775585431/img6_jcli6u.png', alt: 'Demo Image 2' },
  ];

  // Auto-rotate carousel
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide(prev => (prev + 1) % images.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [images.length]);

  const handlePrevSlide = () => {
    setCurrentSlide(prev => (prev - 1 + images.length) % images.length);
  };

  const handleNextSlide = () => {
    setCurrentSlide(prev => (prev + 1) % images.length);
  };

  const handleDotClick = (index) => {
    setCurrentSlide(index);
  };

  const handleImageError = (index) => {
    setImageErrors(prev => ({ ...prev, [index]: true }));
    console.error(`Image ${index} failed to load: ${images[index].src}`);
  };

  useEffect(() => {
    console.log('Carousel mounted with', images.length, 'images');
    console.log('Current slide:', currentSlide);
    images.forEach((img, i) => {
      const testImg = new Image();
      testImg.onload = () => console.log(`Image ${i} loaded successfully from: ${img.src}`);
      testImg.onerror = () => console.error(`Image ${i} failed to load: ${img.src}`);
      testImg.src = img.src;
    });
  }, [images, currentSlide]);

  return (
    <div className="hero-carousel">
      {/* Carousel track */}
      <div className="carousel-track">
        {images.map((img, i) => (
          <div
            key={i}
            className={`carousel-slide ${i === currentSlide ? 'active' : ''}`}
          >
            {imageErrors[i] ? (
              <div className="carousel-image-error">
                Image failed to load
              </div>
            ) : (
              <img 
                src={img.src} 
                alt={img.alt}
                loading="eager"
                className="carousel-image"
                onError={() => handleImageError(i)}
              />
            )}
            <div className="carousel-overlay" aria-hidden />
          </div>
        ))}
      </div>

      {/* Navigation dots */}
      <div className="carousel-dots">
        {images.map((_, i) => (
          <button
            key={i}
            className={`dot ${i === currentSlide ? 'active' : ''}`}
            onClick={() => handleDotClick(i)}
            aria-label={`Go to slide ${i + 1}`}
            aria-current={i === currentSlide ? 'true' : 'false'}
          />
        ))}
      </div>

      {/* Navigation arrows */}
      <button
        className="carousel-nav prev"
        onClick={handlePrevSlide}
        aria-label="Previous slide"
      >
        ‹
      </button>
      <button
        className="carousel-nav next"
        onClick={handleNextSlide}
        aria-label="Next slide"
      >
        ›
      </button>

      {/* Decorative glow */}
      <div className="carousel-glow" aria-hidden />
    </div>
  );
}

/* ══════════════════════════════════════
   MAIN HERO
══════════════════════════════════════ */
const HeroSection = () => {
  return (
    <section className="hero-section">
      <ParticleField />
      <div className="hero-grid-bg" aria-hidden />
      <div className="hero-glow hero-glow-l" aria-hidden />
      <div className="hero-glow hero-glow-r" aria-hidden />
      <div className="hero-glow hero-glow-c" aria-hidden />

      <div className="hero-container">

        {/* ── LEFT ── */}
        <div className="hero-left">

          <div className="hero-badge">
            <span className="badge-dot" />
            🚀 Enterprise AI Platform
          </div>

          <h1 className="hero-title">
            Where <span className="title-white">Human</span><br />
            <span className="title-white">Intelligence</span><br />
            Meets <span className="title-accent">Artificial<br />Brilliance</span>
          </h1>

          <p className="hero-subtitle">
            Transform your business with enterprise-grade AI
            and engineering solutions — deployed in days, not months.
          </p>

          <div className="hero-buttons">
            <Link to="/services"><MagBtn variant="primary">Explore Solutions</MagBtn></Link>
            <Link to="/contact"><MagBtn variant="secondary">Contact Us →</MagBtn></Link>
          </div>

          <ul className="hero-features">
            {[
              "AI-powered SaaS Solutions",
              "Cloud-ready Architecture",
              "Enterprise-grade Security",
            ].map(f => (
              <li key={f} className="feature-item">
                <span className="check-icon" aria-hidden>✓</span>
                {f}
              </li>
            ))}
          </ul>

        </div>

        {/* ── RIGHT ── */}
        <div className="hero-right">
          <HeroCarousel />
        </div>

      </div>

      {/* ── STATS ROW ── */}
      <div className="hero-stats">
        {[
          { to: 95, prefix: "", suffix: "%+", label: "Accuracy Rate" },
          { to: 40, prefix: "", suffix: "%", label: "Cost Reduction" },
          { to: 10, prefix: "", suffix: "x", label: "Faster Processing" },
          { to: 24, prefix: "", suffix: "/7", label: "Intelligent Support" },
        ].map((s, i) => (
          <div key={i} className="stat-item">
            <h3 className="stat-val">
              <Counter to={s.to} prefix={s.prefix} suffix={s.suffix} />
            </h3>
            <p className="stat-lbl">{s.label}</p>
          </div>
        ))}
      </div>

    </section>
  );
};

export default HeroSection;